import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/apiClient";
import { PRODUCT_CATALOG, isProductEnabled } from "@/lib/productCatalog";
import { ClientPicker, AskAnother } from "@/components/TrattativaUI";
import { TrattativaDrawer } from "@/components/TrattativaDrawer";

// Trattative salvate sul portale: una aperta per cliente; quella "corrente" è ricordata nel browser.
const Ctx = createContext(null);
export const useTrattativa = () => useContext(Ctx);

export function TrattativaProvider({ children }) {
  const { user } = useAuth();
  const curKey = `pp_trattativa_cur_${user?.id}`;
  const [list, setList] = useState([]);
  const [curId, setCurId] = useState(null);
  const [pending, setPending] = useState(null);
  const [asked, setAsked] = useState(null);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(() => api.get("/trattative").then(({ data }) => setList(data || [])).catch(() => {}), []);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { if (user?.id) setCurId(localStorage.getItem(curKey)); }, [user?.id, curKey]);
  useEffect(() => { if (!user?.id) return; if (curId) localStorage.setItem(curKey, curId); else localStorage.removeItem(curKey); }, [user?.id, curKey, curId]);
  // nuovo dispositivo: riprende la trattativa aperta più recente
  useEffect(() => { if (list.length && !list.some((t) => t.id === curId)) setCurId(list[0].id); }, [list, curId]);

  const current = list.find((t) => t.id === curId) || null;
  const items = (current?.items || []).filter((c) => isProductEnabled(user, c));

  const saveItems = async (t, next) => {
    try {
      const { data } = await api.put(`/trattative/${t.id}/items`, { items: next });
      setList((l) => l.map((x) => (x.id === t.id ? { ...x, items: data.items } : x)));
      return true;
    } catch (e) { toast.error(apiError(e)); return false; }
  };
  const addTo = async (t, code) => { if (await saveItems(t, [...new Set([...(t.items || []), code])])) setAsked(code); };
  const add = (code) => (current ? addTo(current, code) : setPending(code));
  const startWith = async (c) => {
    try {
      const { data } = await api.post("/trattative", { anagrafica_id: c.id });
      setList((l) => [data, ...l.filter((x) => x.id !== data.id)]);
      setCurId(data.id);
      return data;
    } catch (e) { toast.error(apiError(e)); return null; }
  };
  const pickClient = async (c) => {
    const code = pending;
    setPending(null);
    const t = await startWith(c);
    if (!t) return;
    if (code) addTo(t, code); else setOpen(true);
  };
  const remove = (code) => current && saveItems(current, items.filter((x) => x !== code));
  const annulla = async () => {
    if (!current) return;
    await api.delete(`/trattative/${current.id}`).catch(() => {});
    setCurId(null); refresh();
  };
  const onSent = () => { setCurId(null); refresh(); };

  const value = { current, list, items, add, remove, openDrawer: () => setOpen(true),
    startWith: async (c) => { if (await startWith(c)) setOpen(true); } };
  return (
    <Ctx.Provider value={value}>
      {children}
      <ClientPicker open={pending !== null} onClose={() => setPending(null)} onPick={pickClient} />
      {asked && <AskAnother label={PRODUCT_CATALOG[asked]?.label} onYes={() => setAsked(null)} onNo={() => { setAsked(null); setOpen(true); }} />}
      <TrattativaDrawer open={open} onClose={() => setOpen(false)} current={current} items={items} others={list.filter((t) => t.id !== curId)}
        onSwitch={setCurId} onRemove={remove} onAnnulla={annulla} onSent={onSent} onChangeClient={() => { setOpen(false); setPending(""); }} />
    </Ctx.Provider>
  );
}
